"use client";

import React, { useEffect, useState } from "react";
import useTemplateBase from "./TemplateBase";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Home,
  Tag,
  X,
  Check,
  Loader2,
  Plus,
  Building2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import profile from "../../../public/profilePicture/profile.webp";
import openHouseBadge from "../../assets/star_red.png";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
}

interface Property {
  id: number;
  propertyListType?: {
    id: number;
    list_name: string;
    custom_template_data?: Record<string, any>;
  };
  title: string;
  listing_id?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  photos?: string[] | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template8Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<any>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string;
  readOnly?: boolean;
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

const Template8: React.FC<Template8Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  selectedList: viewSelectedList,
  onPropertyListCreated,
  readOnly = false,
}) => {
  const { toast } = useToast();

  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<string | null>(null);

  const secondaryImageRef = React.useRef<HTMLInputElement>(null);

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    handleFileChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
    };
    reader.readAsDataURL(file);
  };

  const handleSecondaryImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setFlyerData((prev) => ({ ...prev, photo2: dataUrl }));

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const uploadRes = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadRes?.data?.url) {
          updatePhotoAtIndex(uploadRes.data.url, "photo2");
          toast({ title: "Success", description: "Secondary photo uploaded", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload secondary image:", err);
        toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ??
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Featured Listing";

  const allLists =
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Featured Listing" },
        { id: 2, list_name: "Just Listed" },
        { id: 3, list_name: "Open House" },
        { id: 4, list_name: "Exclusive" },
      ];

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  const saveCustomList = async () => {
    if (readOnly) return;
    const trimmedName = customListName.trim();
    if (!trimmedName) {
      toast({ title: "Error", description: "Please enter a name for your custom tag", variant: "destructive" });
      return;
    }
    if (!property?.id) {
      toast({ title: "Error", description: "No property selected", variant: "destructive" });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");
      toast({ title: "List Selected", description: `"${existingList.list_name}" selected` });
      return;
    }

    setIsSavingCustomList(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);
      setLocalPropertyLists((prev) => [...prev, newListType]);
      setIsCustomList(false);
      setCustomListName("");
      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({ title: "Custom Tag Created", description: `"${newListType.list_name}" created` });
    } catch (err: any) {
      toast({ title: "Error", description: "Failed to create custom tag", variant: "destructive" });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;
    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  const images = [
    flyerData.heroImage || "",
    flyerData.photo2 || flyerData.secondaryImage || "",
  ];

  return (
    <div className="bg-background shadow-elevated rounded-xl overflow-hidden w-full max-w-4xl mx-auto border border-border font-arial">

      {/* ── Top Banner ── */}
      <div className="bg-primary text-white py-2 px-3 sm:px-6 flex items-center justify-between gap-2">
        {/* Company name */}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <Home className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
          <span className="font-semibold text-xs sm:text-sm truncate">
            {agentData?.company || "Premier Properties"}
          </span>
        </div>

        {/* Tag Selector */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <Tag className="h-3 w-3 flex-shrink-0" />
          {readOnly ? (
            <span className="bg-cta text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold">
              {selectedList || property?.propertyListType?.list_name || "Featured Listing"}
            </span>
          ) : isCustomList ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={customListName}
                onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                placeholder="Custom tag"
                maxLength={20}
                className="bg-white/20 text-white placeholder-white/60 px-2 py-0.5 rounded text-[10px] sm:text-xs w-20 sm:w-28 outline-none border border-white/30"
                autoFocus
                disabled={isSavingCustomList}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSavingCustomList) { e.preventDefault(); saveCustomList(); }
                  if (e.key === "Escape") cancelCustomList();
                }}
              />
              <button
                onClick={saveCustomList}
                disabled={!customListName.trim() || isSavingCustomList}
                className="p-0.5 sm:p-1 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50"
              >
                {isSavingCustomList
                  ? <Loader2 className="h-3 w-3 text-white animate-spin" />
                  : <Check className="h-3 w-3 text-white" />}
              </button>
              <button onClick={cancelCustomList} disabled={isSavingCustomList} className="p-0.5 sm:p-1 bg-white/20 rounded hover:bg-white/30">
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ) : (
            <select
              value={selectedList}
              onChange={(e) => handleListSelection(e.target.value)}
              className="bg-cta text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold border-none outline-none cursor-pointer max-w-[110px] sm:max-w-none"
            >
              {allLists.map((list) => (
                <option key={list.id} value={list.list_name} className="text-gray-900 bg-white">
                  {list.list_name}
                </option>
              ))}
              <option value="__custom__" className="text-gray-900 bg-white">+ Add Custom</option>
            </select>
          )}
        </div>
      </div>

      {/* ── Two Image Layout ── */}
      <div className="grid grid-cols-2 gap-0.5 sm:gap-1">
        {/* Main Image */}
        <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden group">
          {isOpenHouseConfirmed && (
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 md:top-6 md:left-6 z-20">
              <div className="relative w-14 sm:w-20 md:w-28">
                <img src={openHouseBadge} alt="Open House" className="w-full drop-shadow-xl" />
                <span className="absolute inset-0 flex items-center justify-center text-white font-extrabold text-[8px] sm:text-[10px] md:text-xs tracking-wide text-center pointer-events-none">
                  OPEN<br />HOUSE
                </span>
              </div>
            </div>
          )}

          {flyerData.heroImage ? (
            <img src={flyerData.heroImage} alt="Property view 1" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <ImageIcon className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground" />
            </div>
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="absolute bottom-2 right-2 bg-white/90 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 h-auto"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Change
            </Button>
          )}
          {!readOnly && (
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleMainImageChange} />
          )}
        </div>

        {/* Secondary Image */}
        <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden group">
          {images[1] ? (
            <img src={images[1]} alt="Property view 2" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <ImageIcon className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground" />
            </div>
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="absolute bottom-2 right-2 bg-white/90 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 h-auto"
              onClick={() => secondaryImageRef.current?.click()}
            >
              <Upload className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Change
            </Button>
          )}
          {!readOnly && (
            <input ref={secondaryImageRef} type="file" accept="image/*" className="hidden" onChange={handleSecondaryImageChange} />
          )}
        </div>
      </div>

      {/* ── Price and Address Section ── */}
      <div className="p-3 sm:p-5 md:p-6 bg-gradient-card border-b border-border">

        {/* Headline + Price: stack on mobile, side-by-side on sm+ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2 sm:mb-0">
          {/* Headline */}
          <div className="min-w-0 flex-1">
            {readOnly ? (
              <p
                className="text-lg sm:text-xl md:text-2xl font-bold text-primary
                  line-clamp-1 sm:line-clamp-2 md:line-clamp-none leading-tight"
                title={flyerData.headline || "Property Headline"}
              >
                {flyerData.headline || "Property Headline"}
              </p>
            ) : (
              <input
                type="text"
                value={flyerData.headline || ""}
                onChange={(e) => handleInputChange("headline", e.target.value)}
                className="text-lg sm:text-xl md:text-2xl font-bold text-primary w-full bg-transparent border-none outline-none overflow-hidden text-ellipsis"
                placeholder="Property Headline"
                title={flyerData.headline || ""}
              />
            )}
          </div>

          {/* Price — full width below headline on mobile, right-aligned on sm+ */}
          {readOnly ? (
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-cta sm:text-right flex-shrink-0 leading-tight">
              {flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : "$0"}
            </p>
          ) : (
            <input
              type="text"
              value={flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : ""}
              onChange={(e) => {
                const num = e.target.value.replace(/[^0-9]/g, "");
                handleInputChange("price", num === "" ? null : Number(num));
              }}
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-cta bg-transparent border-none outline-none sm:text-right w-full sm:w-40 md:w-48 flex-shrink-0"
              placeholder="$725,000"
            />
          )}
        </div>

        {/* Address */}
        <div className="flex items-start gap-1.5 sm:gap-2 text-muted-foreground mt-1.5 sm:mt-2">
          <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
          {readOnly ? (
            <span
              className="text-xs sm:text-sm line-clamp-1 sm:line-clamp-2 md:line-clamp-none"
              title={flyerData.address || ""}
            >
              {flyerData.address || "No address provided"}
            </span>
          ) : (
            <input
              type="text"
              value={flyerData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
              className="bg-transparent border-none outline-none text-xs sm:text-sm flex-1 min-w-0"
              placeholder="Property Address"
            />
          )}
        </div>

        {/* Property Stats — scrollable row on very small screens */}
        <div className="flex items-center gap-3 sm:gap-6 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border overflow-x-auto pb-0.5">
          {/* Bedrooms */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Bed className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            {readOnly ? (
              <span className="text-base sm:text-lg font-bold text-primary">
                {flyerData.bedrooms ?? 0}
              </span>
            ) : (
              <input
                type="number"
                min="0"
                value={flyerData.bedrooms ?? ""}
                onChange={(e) => handleInputChange("bedrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-base sm:text-lg font-bold text-primary w-8 sm:w-12 text-center bg-transparent border-none outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                placeholder="0"
              />
            )}
            <span className="text-muted-foreground text-[10px] sm:text-sm whitespace-nowrap">Bedrooms</span>
          </div>

          {/* Bathrooms */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Bath className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            {readOnly ? (
              <span className="text-base sm:text-lg font-bold text-primary">
                {flyerData.bathrooms ?? 0}
              </span>
            ) : (
              <input
                type="number"
                value={flyerData.bathrooms ?? ""}
                onChange={(e) => handleInputChange("bathrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-base sm:text-lg font-bold text-primary w-8 sm:w-12 text-center bg-transparent border-none outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                placeholder="0"
              />
            )}
            <span className="text-muted-foreground text-[10px] sm:text-sm whitespace-nowrap">Bathrooms</span>
          </div>

          {/* Sq Ft */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Square className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            {readOnly ? (
              <span className="text-base sm:text-lg font-bold text-primary">
                {flyerData.sqft ?? flyerData.area ?? 0}
              </span>
            ) : (
              <input
                type="number"
                value={flyerData.sqft ?? flyerData.area ?? ""}
                onChange={(e) => handleInputChange("sqft", e.target.value ? parseInt(e.target.value) : null)}
                className="text-base sm:text-lg font-bold text-primary w-14 sm:w-20 text-center bg-transparent border-none outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                placeholder="0"
              />
            )}
            <span className="text-muted-foreground text-[10px] sm:text-sm whitespace-nowrap">Sq Ft</span>
          </div>
        </div>
      </div>

      {/* ── Description ── */}
      <div className="p-3 sm:p-5 md:p-6">
        {readOnly ? (
          <p className="text-foreground leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
            {flyerData.description || "No description provided."}
          </p>
        ) : (
          <textarea
            value={flyerData.description || ""}
            onChange={(e) => handleInputChange("description", e.target.value)}
            className="w-full bg-transparent border-none outline-none resize-none text-foreground leading-relaxed text-sm sm:text-base"
            rows={3}
            placeholder="Property description..."
          />
        )}
      </div>

      {/* ── Features ── */}
      <div className="px-3 sm:px-5 md:px-6 pb-3 sm:pb-5 md:pb-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-bold text-primary">Key Features</h3>
          {!readOnly && (
            <span className="text-[10px] sm:text-xs text-muted-foreground">
              {flyerData.features?.length || 0}/10
            </span>
          )}
        </div>

        {/* 1-col on mobile, 2-col on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {(flyerData.features || []).map((feature: FeatureItem, index: number) => (
            <div
              key={feature.id ?? index}
              className="flex items-center p-2 sm:p-3 bg-primary/5 rounded-lg border border-primary/10 group min-w-0"
            >
              <div className="w-2 h-2 bg-cta rounded-full mr-2 sm:mr-3 flex-shrink-0" />
              {readOnly ? (
                <span className="text-xs sm:text-sm font-medium truncate">
                  {feature.feature_name || "Feature"}
                </span>
              ) : (
                <>
                  <input
                    type="text"
                    value={feature.feature_name}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    onBlur={() => handleFeatureBlur(index)}
                    maxLength={30}
                    placeholder="Feature (max 30 chars)..."
                    className="w-full bg-transparent border-none outline-none text-xs sm:text-sm font-medium min-w-0"
                  />
                  <button
                    onClick={() => handleFeatureDelete(index)}
                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 ml-1 sm:ml-2 flex-shrink-0"
                  >
                    <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {!readOnly && (flyerData.features?.length || 0) < 10 && (
          <Button variant="outline" size="sm" onClick={addFeature} className="mt-3 sm:mt-4 w-full text-xs sm:text-sm">
            <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Add Feature
          </Button>
        )}
      </div>

      {/* ── Agent Footer ── */}
      <div className="bg-primary/5 p-3 sm:p-5 md:p-6 border-t border-border">
        {/*
          Layout:
          - Mobile: avatars row on top, then agent info below (stacked)
          - sm+: avatars left, info right (side-by-side)
        */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">

          {/* Avatars */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Agent Photo */}
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-muted rounded-full flex items-center justify-center border-2 border-primary/20 overflow-hidden">
              {agentData?.avatar ? (
                <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover" />
              ) : (
                <img src={profile} alt="Default" className="w-full h-full object-cover" />
              )}
            </div>

            {/* Company Logo */}
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white rounded-full border border-gray-200 shadow-md overflow-hidden flex items-center justify-center">
              {agentData?.company_logo ? (
                <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover rounded-full" />
              ) : (
                <span className="text-[8px] sm:text-xs text-gray-400 text-center px-1">Logo</span>
              )}
            </div>
          </div>

          {/* Agent Info */}
          <div className="flex-1 min-w-0">
            {/* Agent Name */}
            {readOnly ? (
              <p className="text-base sm:text-lg font-bold text-primary truncate">
                {flyerData.agentName || "Agent Name"}
              </p>
            ) : (
              <input
                type="text"
                value={flyerData.agentName || ""}
                onChange={(e) => handleInputChange("agentName", e.target.value)}
                className="text-base sm:text-lg font-bold text-primary w-full bg-transparent border-none outline-none"
                placeholder="Agent Name"
              />
            )}

            {/* Contact details — wrap on small screens */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 sm:gap-4 text-xs sm:text-sm text-muted-foreground mt-1">

              {/* Phone */}
              <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
                <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                {readOnly ? (
                  <span className="truncate">{flyerData.agentPhone || "N/A"}</span>
                ) : (
                  <input
                    type="text"
                    value={flyerData.agentPhone || ""}
                    onChange={(e) => handleInputChange("agentPhone", e.target.value)}
                    className="bg-transparent border-none outline-none min-w-0 w-28 sm:w-auto text-xs sm:text-sm"
                    placeholder="Phone"
                  />
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
                <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                {readOnly ? (
                  <span className="truncate max-w-[140px] sm:max-w-none">{flyerData.agentEmail || "N/A"}</span>
                ) : (
                  <input
                    type="text"
                    value={flyerData.agentEmail || ""}
                    onChange={(e) => handleInputChange("agentEmail", e.target.value)}
                    className="bg-transparent border-none outline-none min-w-0 w-32 sm:w-auto text-xs sm:text-sm"
                    placeholder="Email"
                  />
                )}
              </div>

              {/* Company */}
              <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
                <Building2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                {readOnly ? (
                  <span className="truncate">{flyerData.companyName || agentData?.company || "N/A"}</span>
                ) : (
                  <input
                    type="text"
                    value={flyerData.companyName || agentData?.company || ""}
                    onChange={(e) => handleInputChange("companyName", e.target.value)}
                    className="bg-transparent border-none outline-none min-w-0 w-24 sm:w-auto text-xs sm:text-sm"
                    placeholder="Company Name"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      {/* <div className="py-2 sm:py-3 px-3 sm:px-6 text-center text-[10px] sm:text-xs text-muted-foreground border-t border-border">
        <p>Unsubscribe or update your preferences</p>
      </div> */}
    </div>
  );
};

export default Template8;
